import React, { useEffect } from 'react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useResource, useInvalidator, useFetcher } from 'rest-hooks';
import { colors } from '@contently/toolkit/dist/styles/theme';
import { ReplyChain, CommentReply, H6, H5 } from '@contently/toolkit';
import CommentResource from '../../resources/CommentResource';
import { updateCommentListCacheBust } from '../../store/textEditorReducer';
import authorOwnerCheck from '../../utils/authorOwnerCheck';
import useUserContext from '../../hooks/useUserContext';

const ReplyWrapper = styled('div')`
  padding-left: 20px;
  padding-top: 15px;
  display: flex;
  line-height: 16px;
  color: #666;
  background-image: linear-gradient(to right, ${colors.grey.secondaryDark} 33%, transparent 0%);
  background-position: top;
  background-size: 2px 1px;
  background-repeat: repeat-x;
`;

const StyledH5 = styled(H5)`
  font-weight: 400;
  cursor: pointer;
  box-sizing: border-box;
  font-size: 12px;
`;

const DotIcon = styled.span`
  &:before {
    content: '•';
    display: inline-block;
    margin: 0 8px;
    pointer-events: none;
  }
`;

function DefaultReplyView({ numReplies, reply }) {
  if (!reply) return null;
  const numRepliesCopy = numReplies > 1 ? `${numReplies} Replies` : `${numReplies} Reply`;

  return (
    <React.Fragment>
      <ReplyWrapper>
        <H6>{numRepliesCopy}</H6>
        <DotIcon />
        <StyledH5>most recent reply</StyledH5>
      </ReplyWrapper>
      <CommentReply {...reply} defaultView />
    </React.Fragment>
  );
}

const replyTypes = {
  id: PropTypes.number.isRequired,
  body: PropTypes.string.isRequired,
  authorable: PropTypes.shape({
    fullName: PropTypes.string.isRequired,
    avatar: PropTypes.string
  }).isRequired,
  formattedCreatedAt: PropTypes.string.isRequired,
  authorIsEmployee: PropTypes.bool,
  authorIsManagingEditor: PropTypes.bool,
  createdAt: PropTypes.string.isRequired,
  canBeUpdated: PropTypes.bool
};

DefaultReplyView.propTypes = {
  numReplies: PropTypes.number.isRequired,
  reply: PropTypes.shape(replyTypes).isRequired
};

export default function ReplyChainContainer({
  commentId,
  showAllComments,
  showDateCreated,
  enableEdit,
  users,
  sidebar,
  filterState,
  onMentionsFound
}) {
  // Convert commentId to number if it's a string
  const commentIdNum = Number(commentId);

  const dispatch = useDispatch();
  const { permissions } = useUserContext();
  const invalidateCommentListCache = useInvalidator(CommentResource.listShape());
  const editComment = useFetcher(CommentResource.updateShape());
  const { commentListCacheBust } = useSelector(state => state.textEditor);

  // Use the numeric commentId for the API call
  const replies = useResource(CommentResource.listShape(), {
    parent_id: commentIdNum
  });

  // Get current user for mention detection
  const currentUser = users && users.find(user => user.id === permissions.id);

  // Check if any replies mention the current user
  const hasMentions =
    currentUser &&
    replies.some(
      reply =>
        reply.body &&
        (reply.body.includes(`**${currentUser.display}**`) ||
          reply.body.includes(currentUser.display))
    );

  // Notify parent component about mention status (whether found or not)
  useEffect(() => {
    if (onMentionsFound) {
      onMentionsFound(hasMentions);
    }
  }, [hasMentions, onMentionsFound, commentIdNum]);

  const handleReplyEdit = (replyId, updatedData) => {
    editComment({ id: replyId }, { body: updatedData.edit }).then(() => {
      invalidateCommentListCache({ parent_id: commentIdNum });
    });
  };

  const withAvatars = replies
    .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
    .map(reply => {
      const { fullName, avatar } = reply.authorable;

      // Check if this specific reply mentions the current user
      const mentionsUser =
        currentUser &&
        reply.body &&
        (reply.body.includes(`**${currentUser.display}**`) ||
          reply.body.includes(currentUser.display));

      // Convert reply.id to number if it's a string
      const replyId = typeof reply.id === 'string' ? parseInt(reply.id, 10) : reply.id;

      return {
        ...reply,
        id: replyId, // Ensure id is a number
        fullName,
        avatar,
        users,
        canBeUpdated: enableEdit ? authorOwnerCheck(reply.authorable.id, permissions.id) : false,
        showDateCreated: showDateCreated || !(reply.updatedAt > reply.createdAt),
        onEditSubmit: updatedData => handleReplyEdit(replyId, updatedData),
        onEditCancel: () => {},
        // Add data attributes for styling
        'data-reply-id': replyId,
        'data-reply-mentions': mentionsUser && filterState === 'Mentions Me' ? 'true' : 'false'
      };
    });

  const lastReply = replies.length ? withAvatars[replies.length - 1] : null;

  useEffect(() => {
    if (commentListCacheBust && commentListCacheBust === commentIdNum && sidebar) {
      invalidateCommentListCache({ parent_id: commentIdNum });
      dispatch(updateCommentListCacheBust(null));
    }
  }, [commentIdNum, commentListCacheBust, dispatch, invalidateCommentListCache, sidebar]);

  // Always show all comments if we're in "Mentions Me" filter and this thread has mentions
  const shouldShowAllComments = showAllComments || (filterState === 'Mentions Me' && hasMentions);

  return (
    <div data-comment-id={commentIdNum}>
      {shouldShowAllComments ? (
        <ReplyChain replies={withAvatars} />
      ) : (
        lastReply && <DefaultReplyView reply={lastReply} numReplies={replies.length} />
      )}
    </div>
  );
}

ReplyChainContainer.defaultProps = {
  showDateCreated: true,
  enableEdit: false,
  sidebar: false,
  filterState: 'All comments',
  onMentionsFound: null
};

ReplyChainContainer.propTypes = {
  commentId: PropTypes.number.isRequired,
  showAllComments: PropTypes.bool.isRequired,
  showDateCreated: PropTypes.bool,
  enableEdit: PropTypes.bool,
  users: PropTypes.arrayOf(PropTypes.shape({})),
  sidebar: PropTypes.bool,
  filterState: PropTypes.string,
  onMentionsFound: PropTypes.func
};



// WEBPACK FOOTER //
// ./app/javascript/client/components/textEditor/ReplyChainContainer.jsx